import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/v1$/, '');

// EO = Estate Owner, EL = Estate Lawyer, EX/EW = Estate Executor/Witness
export type UserType = 'EO' | 'EL' | 'EX' | 'EW';
type SubscriptionPlan = 'FREE' | 'PRO' | 'PLUS';

interface User {
  id: string;
  email: string;
  name: string;
  userType: UserType;
  subscription: SubscriptionPlan;
  role: SubscriptionPlan; // Backward compatibility
  referralCode?: string;
  referredBy?: string;
  welcomeQuestionnaireCompleted?: boolean;
  hasPendingQuestionnaires?: boolean;
  isBetaUser?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  hasPendingQuestionnaires: boolean;
  pendingQuestionnaireCount: number;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, userType?: string, subscription?: string, referralCode?: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  setHasPendingQuestionnaires: (hasPending: boolean, count?: number) => void;
  checkPendingQuestionnaires: () => Promise<void>;
  lastSeenAlarm: number;
  markAlarmsAsSeen: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      hasPendingQuestionnaires: false,
      pendingQuestionnaireCount: 0,

      login: async (email: string, password: string) => {
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Login failed');

        const { user, accessToken, refreshToken } = data;
        localStorage.setItem('accessToken', accessToken);
        set({
          user,
          accessToken,
          refreshToken,
          hasPendingQuestionnaires: user.hasPendingQuestionnaires || false
        });

        // Check for pending questionnaires after login
        setTimeout(() => get().checkPendingQuestionnaires(), 100);
      },

      loginWithGoogle: async (credential: string) => {
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Google login failed');

        const { user, accessToken, refreshToken } = data;
        localStorage.setItem('accessToken', accessToken);
        set({
          user,
          accessToken,
          refreshToken,
          hasPendingQuestionnaires: user.hasPendingQuestionnaires || false
        });

        // Check for pending questionnaires after login
        setTimeout(() => get().checkPendingQuestionnaires(), 100);
      },

      register: async (email: string, password: string, name: string, userType?: string, subscription?: string, referralCode?: string) => {
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name, userType, subscription, referralCode }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Registration failed');

        const { user, accessToken, refreshToken } = data;
        localStorage.setItem('accessToken', accessToken);
        set({
          user,
          accessToken,
          refreshToken,
          hasPendingQuestionnaires: user.hasPendingQuestionnaires || false
        });

        // Check for pending questionnaires after registration
        setTimeout(() => get().checkPendingQuestionnaires(), 100);
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        localStorage.clear();
        set({ user: null, accessToken: null, refreshToken: null, hasPendingQuestionnaires: false, pendingQuestionnaireCount: 0 });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token');

        const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        const data = await response.json();
        if (!response.ok) {
          localStorage.removeItem('accessToken');
          set({ user: null, accessToken: null, refreshToken: null });
          throw new Error(data.error || 'Refresh failed');
        }

        const { accessToken, refreshToken: newRefreshToken } = data;
        localStorage.setItem('accessToken', accessToken);
        set({ accessToken, refreshToken: newRefreshToken });
      },
      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },
      setHasPendingQuestionnaires: (hasPending, count = 0) => {
        set((state) => ({
          hasPendingQuestionnaires: hasPending,
          pendingQuestionnaireCount: count,
          user: state.user ? { ...state.user, hasPendingQuestionnaires: hasPending } : state.user
        }));
      },
      checkPendingQuestionnaires: async () => {
        const { accessToken, user } = get();
        if (!accessToken || !user) return;

        // Skip for exempt users
        if (user.subscription === 'PLUS' || user.userType === 'EL' || user.userType === 'EX' || user.userType === 'EW') {
          set({ hasPendingQuestionnaires: false, pendingQuestionnaireCount: 0 });
          return;
        }

        // If user has already completed welcome questionnaire (from login response),
        // it means they have no pending assigned questionnaires - trust the login response
        if (user.welcomeQuestionnaireCompleted === true) {
          set({ hasPendingQuestionnaires: false, pendingQuestionnaireCount: 0 });
          return;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/questionnaires/user/check-pending`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            set({
              hasPendingQuestionnaires: data.hasPending,
              pendingQuestionnaireCount: data.pendingCount
            });
          }
        } catch (error) {
          // Silent fail - don't break login flow
          console.error('Failed to check pending questionnaires:', error);
        }
      },
      lastSeenAlarm: 0,
      markAlarmsAsSeen: () => set({ lastSeenAlarm: Date.now() })
    }),
    {
      name: 'auth-storage'
    }
  )
);
